import {
  PropsWithChildren,
  createContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';
import { Text } from '@/components/Text';
import { Modal as TherapistModal } from '@/components/ui/therapist/Modal';
import { PrimaryButton } from '../../../Button';
import {
  DialogContextValue,
  DialogId,
  DialogOptions,
  DialogProps,
  ShowDialogFn,
} from './types';
import { isPatientApp } from '@/config/constants';
import { Button } from '@/components/ui/therapist/Button';
import { Modal as PatientModal } from '@/components/ui/patient/Modal';
import { createDialogButtons, resolveCancelPress } from './createDialogButtons';
import { styles } from './styles';

export const DialogContext = createContext<DialogContextValue | null>(null);
export function DialogProvider({ children }: PropsWithChildren) {
  const [dialogs, setDialogs] = useState<DialogOptions[]>([]);
  const idCounter = useRef(0);

  // Remove a specific dialog by id; with no id, pop the top (the visible one).
  const removeDialog = (id?: DialogId) => {
    setDialogs((p) =>
      id == null ? p.slice(0, -1) : p.filter((d) => d.id !== id),
    );
  };

  const removeAllDialogs = () => {
    setDialogs([]);
  };

  const showDialog: ShowDialogFn = (options: DialogOptions) => {
    const id = ++idCounter.current;
    setDialogs((p) => [...p, { ...options, id }]);
    return id;
  };

  // Replace a still-mounted dialog's options in place (keeps its id/position).
  const updateDialog = (id: DialogId, options: DialogOptions) => {
    setDialogs((p) => p.map((d) => (d.id === id ? { ...options, id } : d)));
  };

  const currentDialogOptions = dialogs[dialogs.length - 1];
  const openDialogIds = dialogs
    .map((d) => d.id)
    .filter((id): id is DialogId => typeof id === 'number');

  return (
    <DialogContext.Provider
      value={{
        showDialog,
        removeDialog,
        removeAllDialogs,
        updateDialog,
        openDialogIds,
      }}
    >
      {children}
      {!!currentDialogOptions && (
        <Dialog
          key={currentDialogOptions.id}
          isVisible
          options={currentDialogOptions}
          onClose={removeDialog}
        />
      )}
    </DialogContext.Provider>
  );
}

function Dialog({ isVisible, onClose, ...props }: DialogProps) {
  const [options, setOptions] = useState(props.options);

  useEffect(() => {
    setOptions(props.options);
  }, [props.options]);

  const {
    content,
    contentComponent,
    title,
    danger,
    headingComponent,
    showCancelX,
    cancelOnBackdropPress,
  } = options;

  const { confirm, cancel } = createDialogButtons(
    options,
    onClose,
    isPatientApp,
  );
  // Both the corner X and the cancel button share this handler, so dismissing
  // via the X runs the same cancelAction side-effects as the cancel button.
  const onCancelPress = resolveCancelPress(options, onClose);

  if (!isPatientApp) {
    return (
      <TherapistModal
        title={title ?? ''}
        isVisible={isVisible}
        onClose={showCancelX ? onCancelPress : undefined}
        cancelActionButton={
          cancel ? (
            <Button
              look="secondary"
              onPress={cancel.onPress}
              label={cancel.label}
            />
          ) : undefined
        }
        confirmActionButton={
          confirm ? (
            <Button
              testID={confirm.testID}
              onPress={confirm.onPress}
              label={confirm.label}
              disabled={confirm.disabled}
            />
          ) : undefined
        }
      >
        {content ? (
          <Text style={styles.contentText}>{content}</Text>
        ) : contentComponent ? (
          contentComponent({ setOptions })
        ) : null}
      </TherapistModal>
    );
  }

  const patientContent = contentComponent ? (
    contentComponent({ setOptions })
  ) : content ? (
    <View style={styles.contentPadding}>
      <Text style={styles.contentText}>{content}</Text>
    </View>
  ) : null;

  return (
    <PatientModal
      title={title}
      isVisible={isVisible}
      onClose={onCancelPress}
      danger={danger}
      headingComponent={headingComponent}
      showCancelX={showCancelX}
      cancelOnBackdropPress={cancelOnBackdropPress}
      boundContent={!!contentComponent}
      confirmActionButton={
        confirm ? (
          <PrimaryButton
            testID={confirm.testID}
            look={confirm.danger ? 'danger' : 'default'}
            label={confirm.label}
            disabled={confirm.disabled}
            onPress={confirm.onPress}
          />
        ) : undefined
      }
      cancelActionButton={
        cancel ? (
          <PrimaryButton
            look="neutral"
            label={cancel.label}
            onPress={cancel.onPress}
          />
        ) : undefined
      }
    >
      {patientContent}
    </PatientModal>
  );
}
